/**
 * Parses the plain labelled text returned on a dynamic-session `run_python` / `run_shell`
 * function-call output. The backend (StorageBackedDynamicSessionRuntimeToolExecutor,
 * FormatCodeResult) emits:
 *
 *   status: {Success|Failure|...}
 *   executionTimeMs: {ms}
 *
 *   stdout:
 *   {stdout}
 *
 *   stderr:
 *   {stderr}
 *
 *   result:
 *   {result}
 *
 *   generatedArtifacts:
 *   - {fileName}: [{fileName}]({url})
 *
 * `generatedArtifacts` is intentionally ignored here — generated files render from the
 * `tool_call_artifacts` metadata on the following message, not from this text.
 */
export interface ParsedDynamicSessionOutput {
    status?: string;
    executionTimeMs?: string;
    stdout?: string;
    stderr?: string;
    result?: string;
    /** True when the backend truncated the output at MaxToolOutputCharacters. */
    truncated: boolean;
    /** The raw output, for fallback rendering. */
    raw: string;
}

const BLOCK_LABELS = new Set([
    'stdout:',
    'stderr:',
    'result:',
    'generatedArtifacts:'
]);

const trimBlock = (lines: string[]): string =>
    lines.join('\n').replace(/^\n+/, '').replace(/\s+$/, '');

const parseDynamicSessionOutput = (
    output: string | undefined | null
): ParsedDynamicSessionOutput => {
    const raw = output ?? '';
    const parsed: ParsedDynamicSessionOutput = {
        truncated: raw.trimEnd().endsWith('... [truncated]'),
        raw
    };

    let current: 'stdout' | 'stderr' | 'result' | null = null;
    let buffer: string[] = [];

    const flush = () => {
        if (current) {
            parsed[current] = trimBlock(buffer);
        }
        buffer = [];
    };

    for (const line of raw.split('\n')) {
        const trimmed = line.trim();

        // The single-line `key: value` headers only appear before any block section.
        if (current === null && trimmed.startsWith('status:')) {
            parsed.status = trimmed.slice('status:'.length).trim();
            continue;
        }
        if (current === null && trimmed.startsWith('executionTimeMs:')) {
            parsed.executionTimeMs = trimmed
                .slice('executionTimeMs:'.length)
                .trim();
            continue;
        }

        if (BLOCK_LABELS.has(trimmed)) {
            flush();
            if (trimmed === 'generatedArtifacts:') {
                // Artifacts are rendered from metadata; nothing after this matters here.
                current = null;
                break;
            }
            current = trimmed.slice(0, -1) as 'stdout' | 'stderr' | 'result';
            continue;
        }

        if (current) {
            buffer.push(line);
        }
    }

    flush();
    return parsed;
};

export default parseDynamicSessionOutput;
